import { inject } from 'aurelia';

import { Helper } from '../resources/extensions/helper';

import { ApiService } from './api-service';

import { IWebsitePage } from '@chicksgroup/types';
import { websiteShortCode } from '../environment';
import { SessionService } from './session-service';

@inject(ApiService, Helper, SessionService)
export class WebsiteService {
    path = 'Website';
    pages: IWebsitePage[];

    constructor(private api: ApiService, private helper: Helper, private sessionService: SessionService) {}

    async getPagesByWebsiteShortcode() {
        if (this.pages) {
            return this.pages;
        }
        this.pages = await this.api.doGet(`${this.path}/Pages?websiteShortCode=${this.sessionService.getWebsiteShortcode() || websiteShortCode()}`) as IWebsitePage[];
        return this.pages;
    }

    getPageById(id: number) {
        if (this.pages) {
            return this.pages.find(x => x.id === id);
        }
        throw new Error('Pages not loaded');
    }

    async getAllBeforeExecution(func) {
        await this.getPagesByWebsiteShortcode();
        return func() as IWebsitePage | IWebsitePage[] | { [Key: string] : IWebsitePage };
    }

    getPageAsync = async(name : string) => {
        return await this.getAllBeforeExecution(() => this.getPage(name)) as IWebsitePage;
    };

    getPage = (name: string) => {
        return this.pages.find(x => x.name.toLowerCase() === name.toLowerCase());
    };

    getRouteAsync = async(name : string) => {
        return await this.getAllBeforeExecution(() => this.getRoute(name));
    };

    getRoute = (name: string) => {
        return this.getPage(name)?.routeName;
    };

    getRouteOrDefaultAsync = async(name : string, def : string|number = -1) => {
        return await this.getAllBeforeExecution(() => this.getRouteOrDefault(name, def));
    };

    getRouteOrDefault = (name : string, def : string|number = -1) => {
        return this.getRoute(name) ?? (def !== -1 ? def : Helper.toRoute(name));
    };

    getManyBy = async(_this, names : string[], extra = 'Page', func = this.getPageAsync) => {
        names.forEach(async name => {
            const variableName = `${this.helper.camelize(name)}${extra}`;
            _this[variableName] = await func(name);
        });
    };

    async getManyPages(_this, names : string[]) {
        await this.getManyBy(_this, names);
    }

    getManyRoutes = async(_this, names : string[]) => {
        await this.getManyBy(_this, names, 'PageRoute', this.getRouteOrDefaultAsync);
    };

    getPagesAndRoutes = async(_this, names : string[]) => {
        await this.getManyPages(_this, names);
        await this.getManyRoutes(_this, names);
    };

    async getPagesDictionary() {
        return await this.getAllBeforeExecution(() => {
            return this.pages.reduce((a, b) => {
                a[b.name] = b;
                return a;
            }, {});
        }) as { [Key: string] : IWebsitePage };
    }
}
